/* ==========================================================================
   Email Obfuscation Utility
   Protects email addresses from spambots and scrapers by avoiding plaintext
   addresses and mailto links in raw HTML, dynamically assembling them via JS.
   ========================================================================== */

function encodeB64(str) {
  if (typeof btoa === 'function') {
    return btoa(unescape(encodeURIComponent(str)));
  }
  return Buffer.from(str, 'utf-8').toString('base64');
}

function decodeB64(str) {
  if (typeof atob === 'function') {
    return decodeURIComponent(escape(atob(str)));
  }
  return Buffer.from(str, 'base64').toString('utf-8');
}

/**
 * Generates an obfuscated email HTML span string.
 * @param {string} user - Email username (e.g. 'sales')
 * @param {string} domain - Email domain (e.g. 'jalud-embedded.com')
 * @param {Object} options - Custom options (text, className, style, fallback, isHtml)
 * @returns {string} HTML markup for the obfuscated email
 */
export function obfuscateEmail(user, domain = 'jalud-embedded.com', options = {}) {
  const text = options.text || `${user}@${domain}`;
  const encUser = encodeB64(user);
  const encDomain = encodeB64(domain);
  const encText = encodeB64(text);
  const className = options.className ? `sed-obf-email ${options.className}` : 'sed-obf-email';
  const styleAttr = options.style ? ` style="${options.style}"` : '';
  const fallback = options.fallback || `${user} [at] ${domain}`;
  const htmlFlag = options.isHtml ? ' data-html="true"' : '';

  return `<span class="${className}" data-u="${encUser}" data-d="${encDomain}" data-t="${encText}"${htmlFlag}${styleAttr}><span class="sed-obf-fallback">${fallback}</span></span>`;
}

/**
 * Dynamically resolves and converts all .sed-obf-email spans in the container into clickable mailto: links.
 * @param {HTMLElement|Document} container - Root element to search within
 */
export function deobfuscateEmails(container = document) {
  if (!container || typeof container.querySelectorAll !== 'function') return;

  const elements = container.querySelectorAll('.sed-obf-email:not([data-deobfuscated])');
  elements.forEach((el) => {
    try {
      const encU = el.getAttribute('data-u');
      const encD = el.getAttribute('data-d');
      const encT = el.getAttribute('data-t');
      const isHtml = el.getAttribute('data-html') === 'true';
      if (!encU || !encD) return;

      const user = decodeB64(encU);
      const domain = decodeB64(encD);
      const displayText = encT ? decodeB64(encT) : `${user}@${domain}`;
      const email = `${user}@${domain}`;

      const link = document.createElement('a');
      link.href = `mailto:${email}`;
      if (isHtml) {
        link.innerHTML = displayText;
      } else {
        link.textContent = displayText;
      }
      link.setAttribute('data-deobfuscated', 'true');

      // Preserve custom styles and classes
      const style = el.getAttribute('style');
      if (style) {
        link.style.cssText = style;
      }
      const classes = el.className.replace('sed-obf-email', '').trim();
      if (classes) {
        link.className = classes;
      }

      el.replaceWith(link);
    } catch (err) {
      console.warn('Could not deobfuscate email:', err);
    }
  });
}
